import { useState, useEffect } from 'react';
import { IconAlertCircleFilled, IconChevronDownFilled } from '@tabler/icons-react';
import { ensureMinLoading } from '../Utils/withLoading';
import { apiFetch } from '../api/client';

const FONT = '"Public Sans", sans-serif';

const inputClass =
  'w-full outline-none bg-white/[0.08] focus:bg-white/[0.12] focus:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)] placeholder:text-white/30 transition-all duration-200 ease-out';

const inputStyle = {
  border: 'none',
  borderRadius: '16px',
  height: '48px',
  padding: '0 16px',
  fontSize: '15px',
  fontWeight: 500,
  fontFamily: FONT,
  color: 'rgba(255, 255, 255, 0.95)',
  width: '100%',
  boxSizing: 'border-box',
};

const labelStyle = {
  fontSize: '13px',
  lineHeight: '18px',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.45)',
  fontFamily: FONT,
};

const saleTypes = [
  { id: 'in_store', label: 'En local' },
  { id: 'pickup', label: 'Recoger' },
  { id: 'delivery', label: 'Domicilio' },
];

function Chip({ selected, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`flex items-center justify-center h-10 px-4 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out ${
        selected ? '' : '[@media(hover:hover)]:hover:bg-white/[0.1]'
      }`}
      style={{
        backgroundColor: selected ? '#fff' : 'rgba(255, 255, 255, 0.06)',
        fontSize: '13px',
        fontWeight: 600,
        fontFamily: FONT,
        color: selected ? '#000' : 'rgba(255, 255, 255, 0.75)',
      }}
    >
      {children}
    </button>
  );
}

function Segment({ selected, onClick, disabled, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={`flex items-center justify-center flex-1 min-w-0 h-10 px-4 rounded-full border-none touch-manipulation select-none transition-colors duration-200 ease-out ${
        selected ? '' : '[@media(hover:hover)]:hover:bg-white/[0.06]'
      }`}
      style={{
        backgroundColor: selected ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
        fontSize: '13px',
        fontWeight: 600,
        fontFamily: FONT,
        color: selected ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.45)',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      <span className="truncate">{children}</span>
    </button>
  );
}

export default function ModalDescuento({ isOpen, onClose, onSaved, dynamicIslandRef, promo = null }) {
  const isEdit = !!promo;

  const [kind, setKind] = useState(promo?.kind || 'category');
  const [form, setForm] = useState({
    nombre: promo?.name || '',
    descripcion: promo?.description || '',
    tipoDescuento: promo?.discount_type || 'percentage',
    valorDescuento: promo && promo.kind === 'category' ? String(promo.discount_value ?? '') : '',
    cantidadMinima: promo?.min_quantity ? String(promo.min_quantity) : '',
    metodosPago: promo?.applies_to_sale_types || ['in_store', 'pickup', 'delivery'],
    fechaInicio: promo?.start_date || '',
    fechaFin: promo?.end_date || '',
    categorias: promo?.categories ? promo.categories.map((c) => c.category_id) : [],
    itemId: promo?.item_id ? String(promo.item_id) : '',
    nuevoPrecio: promo?.new_price != null ? String(promo.new_price) : '',
  });
  const [categoriesList, setCategoriesList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [cRes, pRes] = await Promise.all([
          apiFetch(`/categories`),
          apiFetch(`/products`),
        ]);
        const cats = cRes.ok ? await cRes.json() : [];
        const prods = pRes.ok ? await pRes.json() : [];
        if (!cancelled) {
          setCategoriesList(cats);
          setProductsList(prods);
        }
      } catch {
        if (!cancelled) {
          setCategoriesList([]);
          setProductsList([]);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setKind(promo?.kind || 'category');
    setForm({
      nombre: promo?.name || '',
      descripcion: promo?.description || '',
      tipoDescuento: promo?.discount_type || 'percentage',
      valorDescuento: promo && promo.kind === 'category' ? String(promo.discount_value ?? '') : '',
      cantidadMinima: promo?.min_quantity ? String(promo.min_quantity) : '',
      metodosPago: promo?.applies_to_sale_types || ['in_store', 'pickup', 'delivery'],
      fechaInicio: promo?.start_date || '',
      fechaFin: promo?.end_date || '',
      categorias: promo?.categories ? promo.categories.map((c) => c.category_id) : [],
      itemId: promo?.item_id ? String(promo.item_id) : '',
      nuevoPrecio: promo?.new_price != null ? String(promo.new_price) : '',
    });
    setSaving(false);
    setDeleting(false);
    setError('');
  }, [isOpen, promo]);

  if (!isOpen) return null;

  const selectedProduct = productsList.find((p) => p.id === Number(form.itemId));
  const oldPrice = selectedProduct ? Number(selectedProduct.price) : promo?.old_price ?? null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const toggleMetodoPago = (id) => {
    setForm((prev) => {
      const next = prev.metodosPago.includes(id)
        ? prev.metodosPago.filter((m) => m !== id)
        : [...prev.metodosPago, id];
      return { ...prev, metodosPago: next };
    });
  };

  const toggleCategoria = (id) => {
    setForm((prev) => {
      const next = prev.categorias.includes(id)
        ? prev.categorias.filter((c) => c !== id)
        : [...prev.categorias, id];
      return { ...prev, categorias: next };
    });
  };

  const buildPayload = () => ({
    name: form.nombre.trim(),
    description: form.descripcion.trim() || null,
    applies_to_sale_types: JSON.stringify(
      form.metodosPago.length ? form.metodosPago : saleTypes.map((s) => s.id)
    ),
    start_date: form.fechaInicio || null,
    end_date: form.fechaFin || null,
  });

  const handleSave = async () => {
    if (!form.nombre.trim()) {
      setError('El nombre es obligatorio.');
      return;
    }

    let url;
    let body;
    let method = isEdit ? 'PUT' : 'POST';

    if (kind === 'category') {
      if (form.categorias.length === 0) {
        setError('Selecciona al menos una categoría participante.');
        return;
      }
      const value = form.tipoDescuento === 'two_for_one'
        ? 0
        : parseFloat(form.valorDescuento);
      if (form.tipoDescuento !== 'two_for_one') {
        if (!value || value <= 0) {
          setError('El valor del descuento debe ser mayor a 0.');
          return;
        }
        if (form.tipoDescuento === 'percentage' && value > 100) {
          setError('El porcentaje no puede ser mayor a 100.');
          return;
        }
      }
      url = isEdit
        ? `/category-discounts/${promo.id}`
        : `/category-discounts`;
      body = {
        ...buildPayload(),
        discount_type: form.tipoDescuento,
        discount_value: value,
        min_quantity: parseInt(form.cantidadMinima, 10) || 1,
        categories: form.categorias.map((id) => ({ category_id: id })),
      };
    } else {
      if (!form.itemId) {
        setError('Selecciona el artículo con descuento.');
        return;
      }
      const newPrice = parseFloat(form.nuevoPrecio);
      if (!newPrice || newPrice <= 0) {
        setError('El precio nuevo debe ser mayor a 0.');
        return;
      }
      if (oldPrice != null && newPrice >= oldPrice) {
        setError('El precio nuevo debe ser menor al precio actual del artículo.');
        return;
      }
      url = isEdit
        ? `/individual-discounts/${promo.id}`
        : `/individual-discounts`;
      body = {
        ...buildPayload(),
        item_id: Number(form.itemId),
        old_price: Number(oldPrice),
        new_price: newPrice,
      };
    }

    setSaving(true);
    setError('');
    const startedAt = Date.now();
    dynamicIslandRef?.current?.addAlert('loading', isEdit ? 'Guardando promoción...' : 'Creando promoción...');

    try {
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let msg = 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.';
        try {
          const err = await res.json();
          if (typeof err.detail === 'string') msg = err.detail;
          else if (Array.isArray(err.detail)) msg = err.detail.map((d) => d.msg).join(', ');
        } catch {}
        throw new Error(msg);
      }
      await ensureMinLoading(startedAt);
      dynamicIslandRef?.current?.clearAlerts();
      dynamicIslandRef?.current?.addAlert('success', isEdit ? 'Promoción actualizada correctamente' : 'Promoción creada correctamente');
      onSaved?.();
    } catch (e) {
      await ensureMinLoading(startedAt);
      dynamicIslandRef?.current?.clearAlerts();
      dynamicIslandRef?.current?.addAlert('error', e.message || 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.');
      setError(e.message || 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit || deleting) return;
    setDeleting(true);
    const startedAt = Date.now();
    dynamicIslandRef?.current?.addAlert('loading', 'Eliminando promoción...');
    try {
      const base = kind === 'category' ? 'category-discounts' : 'individual-discounts';
      const res = await apiFetch(`/${base}/${promo.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      await ensureMinLoading(startedAt);
      dynamicIslandRef?.current?.clearAlerts();
      dynamicIslandRef?.current?.addAlert('success', 'Promoción eliminada correctamente');
      onSaved?.();
    } catch {
      await ensureMinLoading(startedAt);
      dynamicIslandRef?.current?.clearAlerts();
      dynamicIslandRef?.current?.addAlert('error', 'No se pudo eliminar la promoción.');
    } finally {
      setDeleting(false);
    }
  };

  const canSave = form.nombre.trim() && !saving;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.72)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="promocion-titulo"
        className="flex flex-col overflow-hidden w-full"
        style={{
          backgroundColor: '#000',
          borderRadius: '28px',
          border: '1px solid rgba(255, 255, 255, 0.16)',
          maxWidth: '920px',
          maxHeight: 'min(760px, calc(100dvh - 24px))',
          boxShadow: '0 40px 120px rgba(0, 0, 0, 0.8)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-5 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-white/[0.16]">
          <h2
            id="promocion-titulo"
            className="font-semibold m-0 truncate"
            style={{
              fontSize: '22px',
              lineHeight: '28px',
              letterSpacing: '-0.02em',
              color: 'rgba(255, 255, 255, 0.95)',
              fontFamily: FONT,
            }}
          >
            {isEdit ? 'Editar promoción' : 'Nueva promoción'}
          </h2>
          <p
            className="m-0 mt-1 truncate"
            style={{
              fontSize: '13px',
              lineHeight: '18px',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.45)',
              fontFamily: FONT,
            }}
          >
            {isEdit ? promo.name : 'Descuentos por categoría o artículo'}
          </p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pedidos-scroll px-4 sm:px-6 py-5">
          {error && (
            <div
              role="alert"
              className="flex items-start gap-2.5 rounded-2xl p-3.5 mb-5"
              style={{ backgroundColor: 'rgba(255, 69, 58, 0.08)' }}
            >
              <IconAlertCircleFilled size={18} color="#ff453a" style={{ flexShrink: 0, marginTop: '1px' }} />
              <span
                style={{
                  fontSize: '13px',
                  lineHeight: '19px',
                  fontWeight: 500,
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: FONT,
                }}
              >
                {error}
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <span style={labelStyle}>Tipo de promoción</span>
                <div
                  className="flex gap-1 p-1 rounded-full"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.035)' }}
                >
                  <Segment
                    selected={kind === 'category'}
                    disabled={isEdit}
                    onClick={() => { if (!isEdit) setKind('category'); }}
                  >
                    Por categoría
                  </Segment>
                  <Segment
                    selected={kind === 'individual'}
                    disabled={isEdit}
                    onClick={() => { if (!isEdit) setKind('individual'); }}
                  >
                    Individual
                  </Segment>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="promo-nombre" style={labelStyle}>Nombre *</label>
                <input
                  id="promo-nombre"
                  type="text"
                  value={form.nombre}
                  onChange={(e) => handleChange('nombre', e.target.value)}
                  placeholder={kind === 'category' ? 'Ej: 15% en postres' : 'Ej: Espresso en oferta'}
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="promo-descripcion" style={labelStyle}>Descripción</label>
                <textarea
                  id="promo-descripcion"
                  value={form.descripcion}
                  onChange={(e) => handleChange('descripcion', e.target.value)}
                  placeholder="Describe la promoción..."
                  rows={2}
                  className={`${inputClass} resize-y`}
                  style={{
                    ...inputStyle,
                    borderRadius: '18px',
                    height: 'auto',
                    padding: '14px 16px',
                    minHeight: '84px',
                  }}
                />
              </div>

              {kind === 'category' ? (
                <>
                  <div className="flex flex-col gap-2">
                    <span style={labelStyle}>Mecánica</span>
                    <div className="flex flex-wrap gap-2">
                      <Chip
                        selected={form.tipoDescuento === 'percentage'}
                        onClick={() => handleChange('tipoDescuento', 'percentage')}
                      >
                        Porcentaje %
                      </Chip>
                      <Chip
                        selected={form.tipoDescuento === 'fixed_amount'}
                        onClick={() => handleChange('tipoDescuento', 'fixed_amount')}
                      >
                        Monto fijo $
                      </Chip>
                      <Chip
                        selected={form.tipoDescuento === 'two_for_one'}
                        onClick={() => handleChange('tipoDescuento', 'two_for_one')}
                      >
                        2x1
                      </Chip>
                    </div>
                  </div>

                  {form.tipoDescuento !== 'two_for_one' && (
                    <div className="flex flex-col gap-2">
                      <label htmlFor="promo-valor" style={labelStyle}>
                        {form.tipoDescuento === 'percentage' ? 'Porcentaje de descuento' : 'Monto de descuento por artículo'}
                      </label>
                      <input
                        id="promo-valor"
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.valorDescuento}
                        onChange={(e) => handleChange('valorDescuento', e.target.value)}
                        placeholder={form.tipoDescuento === 'percentage' ? 'Ej: 15' : 'Ej: 20'}
                        className={inputClass}
                        style={inputStyle}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col gap-2">
                  <label htmlFor="promo-articulo" style={labelStyle}>Artículo *</label>
                  <div className="relative">
                    <select
                      id="promo-articulo"
                      value={form.itemId}
                      onChange={(e) => handleChange('itemId', e.target.value)}
                      disabled={isEdit}
                      className={`appearance-none cursor-pointer ${inputClass}`}
                      style={{
                        ...inputStyle,
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        paddingRight: '48px',
                        cursor: isEdit ? 'default' : 'pointer',
                        opacity: isEdit && !form.itemId ? 0.6 : 1,
                      }}
                    >
                      <option value="" style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>
                        Selecciona un artículo
                      </option>
                      {productsList.map((p) => (
                        <option key={p.id} value={p.id} style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>
                          {p.name} — ${Number(p.price).toFixed(2)}
                        </option>
                      ))}
                    </select>
                    <span
                      className="flex items-center justify-center w-6 h-6 rounded-full"
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'rgba(255, 255, 255, 0.12)',
                        pointerEvents: 'none',
                      }}
                    >
                      <IconChevronDownFilled size={12} color="rgba(255, 255, 255, 0.7)" />
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-5">
              {kind === 'category' ? (
                <>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="promo-cantidad" style={labelStyle}>Cantidad mínima de artículos</label>
                    <input
                      id="promo-cantidad"
                      type="number"
                      min="1"
                      step="1"
                      value={form.cantidadMinima}
                      onChange={(e) => handleChange('cantidadMinima', e.target.value)}
                      placeholder="Ej: 2"
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <span style={labelStyle}>Categorías participantes *</span>
                    <div className="flex flex-wrap gap-2">
                      {categoriesList.map((cat) => (
                        <Chip
                          key={cat.id}
                          selected={form.categorias.includes(cat.id)}
                          onClick={() => toggleCategoria(cat.id)}
                        >
                          {cat.name}
                        </Chip>
                      ))}
                      {categoriesList.length === 0 && (
                        <span
                          style={{
                            fontSize: '13px',
                            fontWeight: 500,
                            color: 'rgba(255, 255, 255, 0.35)',
                            fontFamily: FONT,
                          }}
                        >
                          No hay categorías disponibles
                        </span>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex gap-4">
                  <div className="flex flex-col gap-2 flex-1" style={{ minWidth: 0 }}>
                    <label htmlFor="promo-precio-actual" style={labelStyle}>Precio actual</label>
                    <input
                      id="promo-precio-actual"
                      type="text"
                      value={oldPrice != null ? `$${Number(oldPrice).toFixed(2)}` : ''}
                      readOnly
                      placeholder="$0.00"
                      className={inputClass}
                      style={{ ...inputStyle, opacity: 0.6, cursor: 'default' }}
                    />
                  </div>
                  <div className="flex flex-col gap-2 flex-1" style={{ minWidth: 0 }}>
                    <label htmlFor="promo-precio-nuevo" style={labelStyle}>Precio con descuento *</label>
                    <input
                      id="promo-precio-nuevo"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.nuevoPrecio}
                      onChange={(e) => handleChange('nuevoPrecio', e.target.value)}
                      placeholder="$0.00"
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <span style={labelStyle}>Métodos de venta aplicables</span>
                <div className="flex flex-wrap gap-2">
                  {saleTypes.map((st) => (
                    <Chip
                      key={st.id}
                      selected={form.metodosPago.includes(st.id)}
                      onClick={() => toggleMetodoPago(st.id)}
                    >
                      {st.label}
                    </Chip>
                  ))}
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex flex-col gap-2 flex-1" style={{ minWidth: 0 }}>
                  <label htmlFor="promo-fecha-inicio" style={labelStyle}>Fecha inicio</label>
                  <input
                    id="promo-fecha-inicio"
                    type="date"
                    value={form.fechaInicio}
                    onChange={(e) => handleChange('fechaInicio', e.target.value)}
                    className={inputClass}
                    style={{ ...inputStyle, colorScheme: 'dark' }}
                  />
                </div>
                <div className="flex flex-col gap-2 flex-1" style={{ minWidth: 0 }}>
                  <label htmlFor="promo-fecha-fin" style={labelStyle}>Fecha fin</label>
                  <input
                    id="promo-fecha-fin"
                    type="date"
                    value={form.fechaFin}
                    onChange={(e) => handleChange('fechaFin', e.target.value)}
                    className={inputClass}
                    style={{ ...inputStyle, colorScheme: 'dark' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 shrink-0 px-4 sm:px-6 py-4 border-t border-white/[0.16]">
          {isEdit ? (
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting || saving}
              className="flex items-center justify-center h-12 px-6 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:brightness-110"
              style={{
                backgroundColor: '#ff453a',
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: '#fff',
                fontFamily: FONT,
                cursor: deleting || saving ? 'not-allowed' : 'pointer',
                opacity: deleting || saving ? 0.5 : 1,
              }}
            >
              {deleting ? 'Eliminando…' : 'Eliminar'}
            </button>
          ) : (
            <span className="hidden sm:block" />
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex items-center justify-center h-12 px-6 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-white/[0.85]"
              style={{
                backgroundColor: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: '#000',
                fontFamily: FONT,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1,
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              className="flex items-center justify-center h-12 px-7 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-[#0077ed]"
              style={{
                backgroundColor: canSave ? '#0071e3' : 'rgba(255, 255, 255, 0.06)',
                fontSize: '15px',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                fontFamily: FONT,
                color: canSave ? '#fff' : 'rgba(255, 255, 255, 0.35)',
                cursor: canSave ? 'pointer' : 'not-allowed',
              }}
            >
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
